import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Image, Upload, X, Search, Loader2, Sparkles, FolderOpen } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * CMS Image Picker Component
 * Shows a button that opens a modal with 3 options: Upload, Browse S3, AI Generate
 * Matches the style of MenuManagement product image picker
 */
const CMSImagePicker = ({ 
  value, 
  onChange, 
  label = "Image",
  buttonLabel = "Add Image",
  showInline = false,
  className = ""
}) => {
  // Main modal state
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  
  // Upload state
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  // Browse S3 state
  const [showBrowseModal, setShowBrowseModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [s3Images, setS3Images] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  
  // AI Generate state
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiDescription, setAiDescription] = useState('');
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  
  // Image format/aspect ratio state
  const [selectedImageFormat, setSelectedImageFormat] = useState('1:1');
  const imageFormats = [
    { id: '1:1', name: '1:1 (Square)', size: '1024x1024' },
    { id: '16:9', name: '16:9 (Landscape)', size: '1792x1024' },
    { id: '9:16', name: '9:16 (Portrait)', size: '1024x1792' }
  ];
  
  // Image model state - default to common models (same as MenuManagement)
  const [availableImageModels, setAvailableImageModels] = useState([
    { id: 'google/gemini-2.0-flash-exp-image-generation', name: 'Nano Banana (Gemini 2.0 Flash)' },
    { id: 'google/imagen-4.0-generate-001', name: 'Nano Banana Pro (Imagen 4)' },
    { id: 'dall-e-3', name: 'DALL-E 3 (OpenAI)' },
    { id: 'dall-e-2', name: 'DALL-E 2 (OpenAI)' },
    { id: 'gpt-image-1', name: 'GPT Image 1 (OpenAI)' }
  ]);
  const [selectedImageModel, setSelectedImageModel] = useState('google/gemini-2.0-flash-exp-image-generation');
  const [loadingModels, setLoadingModels] = useState(false);

  const getToken = () => localStorage.getItem('token');

  // Load available image models from backend settings (same logic as MenuManagement)
  useEffect(() => {
    const loadImageModels = async () => {
      try {
        setLoadingModels(true);
        const response = await fetch(`${API_BASE_URL}/api/openai-settings`, {
          headers: { 'Authorization': `Bearer ${getToken()}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings && data.image_models) {
            const models = (data.image_models || []).map(model => ({
              id: model.value || model.id,
              name: model.label || model.name
            }));
            
            if (models.length > 0) {
              setAvailableImageModels(models);
              // Set default model from settings
              const defaultModel = data.settings?.image_generation_model || 'google/gemini-2.0-flash-exp-image-generation';
              setSelectedImageModel(defaultModel);
            }
          }
        }
      } catch (error) {
        console.error('Error loading image models:', error);
        // Keep default models on error
      } finally {
        setLoadingModels(false);
      }
    };
    
    loadImageModels();
  }, []);

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API_BASE_URL}/api/s3/upload?folder_type=cms_images`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.file_url) {
        onChange(response.data.file_url);
        setShowOptionsModal(false);
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Search S3 images
  const handleSearchS3 = async () => {
    if (searchQuery.trim().length < 3) {
      alert('Please enter at least 3 characters to search');
      return;
    }
    
    setHasSearched(true);
    setLoadingImages(true);
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/menu-items/browse-images?search=${encodeURIComponent(searchQuery.trim())}`,
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setS3Images(data.images || []);
      } else {
        console.error('Failed to search images');
        setS3Images([]);
      }
    } catch (error) {
      console.error('Error searching S3:', error);
      setS3Images([]);
    } finally {
      setLoadingImages(false);
    }
  };

  // Select image from S3
  const selectS3Image = (imageUrl) => {
    onChange(imageUrl);
    setShowBrowseModal(false);
    setShowOptionsModal(false);
    setSearchQuery('');
    setS3Images([]);
    setHasSearched(false);
  };

  // Generate AI image
  const handleGenerateAI = async () => {
    if (!aiPrompt.trim()) {
      setAiError('Please enter a prompt for image generation');
      return;
    }

    setGenerating(true);
    setAiError('');

    try {
      const fullPrompt = aiDescription 
        ? `${aiPrompt}. ${aiDescription}` 
        : aiPrompt;
      
      // Get image size based on selected format
      const selectedFormat = imageFormats.find(f => f.id === selectedImageFormat);
      const imageSize = selectedFormat?.size || '1024x1024';

      const response = await axios.post(
        `${API_BASE_URL}/api/ai/generate-image`,
        { 
          prompt: fullPrompt,
          folder_type: 'cms_images',
          model: selectedImageModel,
          size: imageSize
        },
        { headers: { 'Authorization': `Bearer ${getToken()}` } }
      );

      if (response.data.success && response.data.image_url) {
        onChange(response.data.image_url);
        setShowAIModal(false);
        setShowOptionsModal(false);
        setAiPrompt('');
        setAiDescription('');
      } else {
        setAiError(response.data.error || 'Failed to generate image');
      }
    } catch (err) {
      console.error('AI generation error:', err);
      setAiError(err.response?.data?.detail || 'Failed to generate image. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <>
      {/* Add Image Button - Only show if no image exists */}
      {showInline ? (
        // Inline mode: hide button when image exists
        !value && (
          <button
            type="button"
            onClick={() => setShowOptionsModal(true)}
            className={`text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 ${className}`}
          >
            <Image className="h-4 w-4" />
            {buttonLabel}
          </button>
        )
      ) : (
        <div className={className}>
          {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
          {!value ? (
            // Show add button when no image
            <button
              type="button"
              onClick={() => setShowOptionsModal(true)}
              className="w-full px-3 py-2 bg-blue-50 text-blue-600 border border-blue-200 border-dashed rounded-lg hover:bg-blue-100 transition-colors flex items-center justify-center gap-1.5 text-sm font-medium"
            >
              <Image className="h-4 w-4" />
              {buttonLabel}
            </button>
          ) : (
            // Show image preview with remove option when image exists
            <div className="relative rounded-lg overflow-hidden border border-gray-200">
              <img src={value} alt="Preview" className="w-full h-32 object-cover" onError={(e) => e.target.style.display = 'none'} />
              <button
                type="button"
                onClick={() => onChange('')}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                title="Remove image"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Options Modal - Upload, Browse, AI Generate */}
      {showOptionsModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full">
            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between rounded-t-xl" style={{ backgroundColor: 'var(--theme-primary, rgb(41, 173, 211))' }}>
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Image className="h-5 w-5" />
                Add Image
              </h3>
              <button onClick={() => setShowOptionsModal(false)} className="text-white hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Options */}
            <div className="p-5 space-y-3">
              {/* Upload Option */}
              <label className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 cursor-pointer transition-colors">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Upload className="h-6 w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Upload from Computer</p>
                  <p className="text-sm text-gray-500">Select an image file</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                />
                {uploading && <Loader2 className="h-5 w-5 animate-spin text-blue-600" />}
              </label>

              {/* Browse S3 Option */}
              <button
                onClick={() => {
                  setShowOptionsModal(false);
                  setShowBrowseModal(true);
                }}
                className="w-full flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors text-left"
              >
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                  <FolderOpen className="h-6 w-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">Browse S3 Bucket</p>
                  <p className="text-sm text-gray-500">Search existing images</p>
                </div>
              </button>

              {/* AI Generate Option */}
              <button
                onClick={() => {
                  setShowOptionsModal(false);
                  setShowAIModal(true);
                }}
                className="w-full flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors text-left"
              >
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-purple-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">AI Generate</p>
                  <p className="text-sm text-gray-500">Create with AI</p>
                </div>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Browse S3 Modal */}
      {showBrowseModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between rounded-t-xl" style={{ backgroundColor: 'var(--theme-primary, rgb(41, 173, 211))' }}>
              <h3 className="text-lg font-semibold text-white">Browse Images</h3>
              <button onClick={() => { setShowBrowseModal(false); setSearchQuery(''); setS3Images([]); setHasSearched(false); }} className="text-white hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Search Box */}
              <div className="mb-6">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && searchQuery.trim().length >= 3 && handleSearchS3()}
                    placeholder="Enter at least 3 characters to search (e.g., 'chicken', 'pizza')"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={handleSearchS3}
                    disabled={loadingImages || searchQuery.trim().length < 3}
                    className="px-6 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ backgroundColor: 'var(--theme-primary, rgb(41, 173, 211))' }}
                  >
                    {loadingImages ? 'Searching...' : 'Search'}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Tip: Enter at least 3 characters to enable search. If image not found, use "AI Generate" or upload option.
                </p>
              </div>

              {/* Results */}
              {loadingImages ? (
                <div className="text-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto" />
                  <p className="text-gray-500 mt-4">Searching images...</p>
                </div>
              ) : !hasSearched ? (
                <div className="text-center py-12 text-gray-400">
                  <Search className="h-16 w-16 mx-auto mb-4 opacity-50" />
                  <p>Enter a search term to find images</p>
                </div>
              ) : s3Images.length === 0 ? (
                <div className="text-center py-12">
                  <Image className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">No images found matching "{searchQuery}"</p>
                  <p className="text-sm text-gray-500 mt-2">Try a different search term or:</p>
                  <div className="mt-4 flex gap-3 justify-center">
                    <button
                      onClick={() => { setShowBrowseModal(false); setShowAIModal(true); }}
                      className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90"
                      style={{ backgroundColor: 'rgb(147, 51, 234)' }}
                    >
                      🎨 AI Generate Image
                    </button>
                    <label className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer">
                      📤 Upload Image
                      <input type="file" accept="image/*" onChange={handleFileUpload} className="hidden" />
                    </label>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {s3Images.map((image, index) => (
                    <div
                      key={index}
                      onClick={() => selectS3Image(image.url)}
                      className="cursor-pointer group"
                    >
                      <div className="aspect-square rounded-lg overflow-hidden border-2 border-transparent group-hover:border-blue-500 transition-all">
                        <img src={image.url} alt={image.filename || `Image ${index + 1}`} className="w-full h-full object-cover" />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 truncate">{image.filename}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* AI Generate Modal */}
      {showAIModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 flex items-center justify-between rounded-t-xl" style={{ backgroundColor: 'rgb(147, 51, 234)' }}>
              <div className="flex items-center">
                <Sparkles className="h-5 w-5 mr-2 text-white" />
                <h3 className="text-lg font-semibold text-white">AI Generate Image</h3>
              </div>
              <button onClick={() => { setShowAIModal(false); setAiPrompt(''); setAiDescription(''); setAiError(''); }} className="text-white hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start">
                <svg className="w-5 h-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-xs text-blue-800">
                  AI will generate a professional image based on your description. Be specific about style, colors, and composition.
                </p>
              </div>

              {aiError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {aiError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image Subject / Title *
                </label>
                <input
                  type="text"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="e.g., Fresh vegetable salad, Modern restaurant interior"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Description (Optional)
                </label>
                <textarea
                  value={aiDescription}
                  onChange={(e) => setAiDescription(e.target.value)}
                  placeholder="Add details like colors, style, mood, composition..."
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                />
              </div>

              {/* AI Image Model Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI Image Model
                </label>
                <select
                  value={selectedImageModel}
                  onChange={(e) => setSelectedImageModel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm bg-white"
                  disabled={loadingModels}
                >
                  {availableImageModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose which AI model to use for generating the image
                </p>
              </div>
              
              {/* Image Format/Aspect Ratio Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image Format
                </label>
                <div className="flex gap-2">
                  {imageFormats.map((format) => (
                    <button
                      key={format.id}
                      type="button"
                      onClick={() => setSelectedImageFormat(format.id)}
                      className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg border-2 transition-all ${
                        selectedImageFormat === format.id
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      {format.name}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select the aspect ratio for the generated image
                </p>
              </div>
              
              {loadingModels && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading available models...
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3 rounded-b-xl">
              <button
                onClick={() => { setShowAIModal(false); setAiPrompt(''); setAiDescription(''); setAiError(''); }}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                disabled={generating}
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateAI}
                disabled={generating || !aiPrompt.trim()}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: 'rgb(147, 51, 234)' }}
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate Image
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CMSImagePicker;
